import { ColumnsType } from 'antd/es/table';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppLanguage } from 'app/config/i18Config/types';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import {
  checkUserDataComplete,
  getUserName,
  useGetPaginatedUsersListingQuery,
  User,
  UserForListing,
  useRole,
  UserRoles,
} from 'entities/User';
import { ExportEntity, ExportModal, getUserColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { useSendLoginLink } from 'features/SendLoginLink';
import { stepperModalActions } from 'features/StepperModal';
import { TableFilterType, useTableFilterContext } from 'features/TableFilter';
import { UserNoteDrawer, userNoteDrawerActions } from 'features/UserNoteDrawer';
import { FC, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CancelIcon } from 'shared/assets/icons/CancelIcon.svg';
import { ReactComponent as CompletedStepIcon } from 'shared/assets/icons/CompletedStepIcon.svg';
import { ReactComponent as ContractsTableIcon } from 'shared/assets/icons/ContractsTableIcon.svg';
import { ReactComponent as ContractIcon } from 'shared/assets/icons/ContractWhiteIcon.svg';
import { ReactComponent as CustomersIcon } from 'shared/assets/icons/CustomersIcon.svg';
import { ReactComponent as DebtorIcon } from 'shared/assets/icons/DebtorsIcon.svg';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';
import { ReactComponent as EditUserTableIcon } from 'shared/assets/icons/EditUserTableIcon.svg';
import { ReactComponent as EmailIcon } from 'shared/assets/icons/EmailIcon.svg';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ReactComponent as LeadIcon } from 'shared/assets/icons/LeadIcon.svg';
import { ReactComponent as ManagerIcon } from 'shared/assets/icons/ManagerIcon.svg';
import { ReactComponent as NoteTableIcon } from 'shared/assets/icons/NoteTableIcon.svg';
import { ReactComponent as PartnerIcon } from 'shared/assets/icons/PartnerIcon.svg';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ReactComponent as StaffIcon } from 'shared/assets/icons/StaffIcon.svg';
import { ReactComponent as SuperUserIcon } from 'shared/assets/icons/SuperUserIcon.svg';
import { Paragraph } from 'shared/ui/Paragraph';
import { ServerTable } from 'shared/ui/ServerTable';
import { RowBadge } from 'shared/ui/ServerTable/RowBadge';
import { BadgeColor, RowSideBadge } from 'shared/ui/ServerTable/RowSideBadge';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { TabSwitcher } from 'shared/ui/TabSwitcher';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { ChooseUserModal } from 'widgets/ChooseUserModal';
import { CompleteRegistrationModal, completeRegistrationModalActions } from 'widgets/CompleteRegistrationModal';
import { CreateUserModal, createUserModalActions } from 'widgets/CreateUserModal';
import { EmailHistoryModal, useEmailHistoryModal } from 'widgets/EmailHistoryModal';
import { DrawerMode, NoteDrawer, noteDrawerActions } from 'widgets/NoteDrawer';
import { SendFranchiseSectionModal, sendFranchiseSectionModalActions } from 'widgets/SendFranchiseSectionModal';
import { SendOfferModal, sendOfferModalActions } from 'widgets/SendOfferModal';
import { transformUsersListFilters } from '../utils/helpers/transformUsersListFilters';
import { LeadsTable } from './LeadsTable';
import { UsersListFilter } from './UsersListFilter';
import { IssueInvoiceModal, issueInvoiceModalActions } from 'widgets/IssueInvoiceModal';

type TabKey = UserRoles | 'lead' | 'debtor';

export const UsersPageContent: FC = memo(() => {
  const { parsedQuery, decode } = useQueryString();

  const globalSettings = useAppSelector(getGlobalSettings);

  const { isManagement } = useRole();

  const externalFilters = decode(parsedQuery) as TableFilterType;

  const [selectedTabKey, setSelectedTabKey] = useState<TabKey>(externalFilters.tab?.value || 'customer');

  const { openExportModal, isExportEnabled } = useOpenExportModal();

  const navigate = useNavigate();
  const currencySymbol = useGetCurrencySymbol();

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const { t } = useAppTranslation(['user', 'common', 'profile']);

  const { executeSendingLoginLink } = useSendLoginLink();

  const { openEmailHistoryModal } = useEmailHistoryModal();

  const dispatch = useAppDispatch();

  const goToUserContracts = useCallback(
    (user: UserForListing): void => {
      navigate(`${AppRoutes.USERS}/${user.userId}/contracts`);
    },
    [navigate],
  );

  const goToUserProfile = useCallback(
    (user: UserForListing): void => {
      navigate(`${AppRoutes.PROFILE}/${user.userId}`);
    },
    [navigate],
  );

  const openCreateUserModal = useCallback((): void => {
    dispatch(createUserModalActions.setOpened(true));
  }, [dispatch]);

  const openIssueInvoiceModal = useCallback((): void => {
    dispatch(issueInvoiceModalActions.setOpened(true));
  }, [dispatch]);

  const openCompleteRegistrationModal = useCallback(
    (user: UserForListing): void => {
      dispatch(completeRegistrationModalActions.setOpened(user.userId));
    },
    [dispatch],
  );

  const openSendOfferModal = useCallback((): void => {
    dispatch(sendOfferModalActions.setOpened(true));
  }, [dispatch]);

  const handleSelectOfferModalUser = useCallback(
    (user: User): void => {
      dispatch(sendOfferModalActions.setSelectedEmail(user.email));
      dispatch(sendOfferModalActions.setSelectedLanguage(user.language as AppLanguage));
      dispatch(sendOfferModalActions.setSelectedUserId(user.userId));
      dispatch(stepperModalActions.changeActiveStepIndex(1));

      openSendOfferModal();
    },
    [dispatch, openSendOfferModal],
  );

  const opentSendFranchiseSectionModal = useCallback(
    (user: User): void => {
      dispatch(sendFranchiseSectionModalActions.setSelectedEmail(user.email));
      dispatch(sendFranchiseSectionModalActions.setSelectedLanguage(user.language as AppLanguage));
    },
    [dispatch],
  );

  const createNote = useCallback(
    (user: UserForListing): void => {
      dispatch(noteDrawerActions.setOpenNoteDrawer({ mode: DrawerMode.NOTE_CREATE, user }));
    },
    [dispatch],
  );

  const openUserNoteDrawer = useCallback(
    (user: User): void => {
      dispatch(userNoteDrawerActions.setOpenUserNoteDrawer({ user, isNote: true }));
    },
    [dispatch],
  );

  const openUserDebtCollectionNoteDrawer = useCallback(
    (user: User): void => {
      dispatch(userNoteDrawerActions.setOpenUserNoteDrawer({ user, isDebtCollectionNote: true }));
    },
    [dispatch],
  );

  const rowActions: Array<ServerTableRowActions<UserForListing>> = useMemo(
    () => [
      {
        name: 'addNote',
        icon: <NoteTableIcon />,
        theme: 'clear',
        description: t('Add note'),
        onClick: openUserNoteDrawer,
      },
      {
        name: 'viewContracts',
        icon: <ContractsTableIcon />,
        theme: 'clear',
        description: t('View contracts'),
        onClick: goToUserContracts,
      },
      {
        name: 'editUser',
        icon: <EditUserTableIcon />,
        theme: 'clear',
        description: t('Edit user'),
        onClick: goToUserProfile,
      },
      {
        name: 'sendLoginLink',
        icon: <EmailIcon className="[&>path]:stroke-primary" />,
        theme: 'clear',
        label: t('Send login link'),
        description: t('Send login link'),
        onClick: (selectedUser) => {
          executeSendingLoginLink({ userId: selectedUser.userId });
        },
      },
      {
        name: 'completeRegistration',
        icon: <CompletedStepIcon className="[&>path]:fill-primary" />,
        theme: 'clear',
        label: t('Complete registration'),
        description: t('Complete registration'),
        isDisabled: (item) => checkUserDataComplete(item, item.accountType, globalSettings),
        onClick: openCompleteRegistrationModal,
      },
      {
        name: 'sendOffer',
        theme: 'clear',
        label: t('Send offer'),
        description: t('Send offer'),
        icon: <ContractIcon className="[&>path]:fill-primary" />,
        onClick: handleSelectOfferModalUser,
      },
      {
        name: 'createNote',
        theme: 'clear',
        label: t('Create Note'),
        description: t('Create Note'),
        icon: <PlusIcon className="[&>path]:stroke-primary" />,
        onClick: createNote,
      },
      {
        name: 'addDebtCollectionNote',
        icon: <NoteTableIcon />,
        label: t('Debt Collection Note'),
        description: t('Debt Collection Note'),
        theme: 'clear',
        onClick: openUserDebtCollectionNoteDrawer,
      },
      {
        name: 'emailHistory',
        theme: 'clear',
        label: t('Email history'),
        description: t('Email history'),
        icon: <DocumentIcon className="[&>path]:stroke-primary" />,
        onClick: (selectedUser) => {
          selectedUser.email && openEmailHistoryModal(selectedUser.email);
        },
      },
    ],
    [
      createNote,
      executeSendingLoginLink,
      globalSettings,
      goToUserContracts,
      goToUserProfile,
      handleSelectOfferModalUser,
      openCompleteRegistrationModal,
      openEmailHistoryModal,
      openUserDebtCollectionNoteDrawer,
      openUserNoteDrawer,
      t,
    ],
  );

  const partnerRowActions: Array<ServerTableRowActions<UserForListing>> = useMemo(
    () => [
      {
        name: 'addNote',
        icon: <NoteTableIcon />,
        theme: 'clear',
        description: t('Add note'),
        onClick: openUserNoteDrawer,
      },
      {
        name: 'editUser',
        icon: <EditUserTableIcon />,
        theme: 'clear',
        description: t('Edit user'),
        onClick: goToUserProfile,
      },
      {
        name: 'sendLoginLink',
        icon: <EmailIcon className="[&>path]:stroke-primary" />,
        theme: 'clear',
        label: t('Send login link'),
        description: t('Send login link'),
        onClick: (selectedUser) => {
          executeSendingLoginLink({ userId: selectedUser.userId });
        },
      },
      {
        name: 'sendFranchiseSection',
        theme: 'clear',
        label: t('Send franchise section'),
        description: t('Send franchise section'),
        icon: <ContractIcon className="[&>path]:fill-primary" />,
        onClick: opentSendFranchiseSectionModal,
      },
      {
        name: 'emailHistory',
        theme: 'clear',
        label: t('Email history'),
        description: t('Email history'),
        icon: <DocumentIcon className="[&>path]:stroke-primary" />,
        onClick: (selectedUser) => {
          selectedUser.email && openEmailHistoryModal(selectedUser.email);
        },
      },
    ],
    [executeSendingLoginLink, goToUserProfile, openEmailHistoryModal, opentSendFranchiseSectionModal, t, openUserNoteDrawer],
  );

  const customerColumns = useMemo<ColumnsType<UserForListing>>(
    () => [
      {
        title: t('Name'),
        key: 'name',
        width: '15%',
        render: (_, record) => {
          const isUserDataCompleted = checkUserDataComplete(record, record.accountType, globalSettings);

          const badgeColor: BadgeColor = 'error';
          const badgeTitle = t('Debt collection proccess');

          return (
            <div className="text-primary font-semibold">
              {getUserName(record)}
              {record.isInDebtCollection && <RowBadge color={badgeColor} title={badgeTitle} />}
              {!isUserDataCompleted && (
                <RowSideBadge color="error" toolTipColor="white" toolTipFontColor="primary" title={t('Uncompleted registration')} />
              )}
            </div>
          );
        },
      },
      { title: t('Email'), key: 'email', render: (_, record) => record.email },
      { title: t('Phone'), key: 'phone', render: (_, record) => record.phone },
      {
        title: t('Date'),
        key: 'createdAt',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => getClientDateFormat(record.createdAt),
      },
      { title: t('Account type'), key: 'accountType', render: (_, record) => t(record.accountType) },
      {
        title: t('Balance'),
        key: 'balance',
        render: (_, record) => (
          <div>
            {record.email && (
              <div className="text-success font-semibold">{record.balance ? `${record.balance} ${currencySymbol}` : ''}</div>
            )}
          </div>
        ),
      },
      {
        title: t('Debt'),
        key: '"userDebt"',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div className="text-error font-semibold">{record.userDebt ? `${record.userDebt} ${currencySymbol}` : ''}</div>
        ),
      },
      {
        title: t('Note'),
        key: 'note',
        render: (_, record) => {
          const paragraphs = record.note?.split('\n');

          const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
            <p key={index} className="font-normal !mb-0">
              {paragraph}
            </p>
          ));

          return <Paragraph rows={3}>{paragraphElements}</Paragraph>;
        },
      },
    ],
    [currencySymbol, globalSettings, t],
  );

  const columns = useMemo<ColumnsType<UserForListing>>(
    () => [
      {
        title: t('Name'),
        key: 'name',
        width: '15%',
        render: (_, record) => {
          const isUserDataCompleted = checkUserDataComplete(record, record.accountType, globalSettings);

          return (
            <div className="text-primary font-semibold">
              {getUserName(record)}
              {!isUserDataCompleted && (
                <RowSideBadge color="error" toolTipColor="white" toolTipFontColor="primary" title={t('Uncompleted registration')} />
              )}
            </div>
          );
        },
      },
      { title: t('Email'), key: 'email', render: (_, record) => record.email },
      { title: t('Phone'), key: 'phone', render: (_, record) => record.phone },
      {
        title: t('Date'),
        key: 'createdAt',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => getClientDateFormat(record.createdAt),
      },
      {
        title: t('Warehouses'),
        key: 'warehousesNames',
        render: (_, record) => (
          <div className="font-normal text-primary">
            {record.warehousesNames?.map((name) => {
              return <div key={name}>{name}</div>;
            })}
          </div>
        ),
      },
    ],
    [globalSettings, t],
  );

  const partnerColumns = useMemo<ColumnsType<UserForListing>>(
    () => [
      {
        title: t('Name'),
        key: 'name',
        render: (_, record) => {
          const isNewPartner = !record.warehousesNames;

          let badgeTitle = t('Lead');

          let badgeColor: BadgeColor = 'success';

          if (record.warehousesNames?.length > 0) {
            badgeColor = 'warn';
            badgeTitle = t('Partner');
          }

          return (
            <div className="my-2">
              {getUserName(record)}
              {isNewPartner && <sup className="text-sm text-success p-1">{t('New')}</sup>}
              <RowBadge color={badgeColor} title={badgeTitle} />
            </div>
          );
        },
      },
      { title: t('Email'), key: 'email', render: (_, record) => record.email },
      { title: t('Phone'), key: 'phone', render: (_, record) => record.phone },
      {
        title: t('Date'),
        key: 'createdAt',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => getClientDateFormat(record.createdAt),
      },
      {
        title: t('Warehouses'),
        key: 'warehousesNames',
        render: (_, record) => (
          <div className="font-normal text-primary">
            {record.warehousesNames?.map((name) => {
              return <div key={name}>{name}</div>;
            })}
          </div>
        ),
      },
      {
        title: t('Note'),
        key: 'note',
        render: (_, record) => {
          const paragraphs = record.note?.split('\n');

          const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
            <p key={index} className="font-normal !mb-0">
              {paragraph}
            </p>
          ));

          return <Paragraph rows={3}>{paragraphElements}</Paragraph>;
        },
      },
    ],
    [t],
  );

  const tabs = useMemo(
    () => [
      { key: 'customer', label: t('Customers'), icon: <CustomersIcon /> },
      { key: 'debtor', label: t('Debtors'), icon: <DebtorIcon /> },
      { key: 'manager', label: t('Managers'), icon: <ManagerIcon /> },
      { key: 'superuser', label: t('Superusers'), icon: <SuperUserIcon /> },
      { key: 'partner', label: t('Partners'), icon: <PartnerIcon /> },
      { key: 'staff', label: t('Staff'), icon: <StaffIcon /> },
      { key: 'lead', label: t('Leads'), icon: <LeadIcon /> },
    ],
    [t],
  );

  const tableActions: Array<ServerTableActions<UserForListing>> = useMemo(() => {
    const exportAction: Array<ServerTableActions<UserForListing>> = isExportEnabled
      ? [
          {
            name: t('Export', { ns: 'common' }),
            theme: 'secondary',
            icon: <ExportIcon />,
            iconPosition: 'prev',
            onClick: () => {
              const isRole = Object.values(UserRoles).includes(selectedTabKey as UserRoles);

              const role = isRole ? selectedTabKey : UserRoles.CUSTOMER;

              const filters: AnyObject = { ...transformUsersListFilters(appliedFilters), role };

              if (selectedTabKey === 'debtor') {
                filters.debt = true;
              }

              openExportModal({
                filters,
                columns: getUserColumnKeys(t),
                entity: ExportEntity.USER,
              });
            },
          },
        ]
      : [];

    return [
      {
        name: t('Add user'),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: openCreateUserModal,
      },
      {
        name: t('Issue Invoice'),
        icon: <ContractIcon className="[&>path]:fill-white" />,
        iconPosition: 'prev',
        onClick: openIssueInvoiceModal,
        disabled: !isManagement,
        theme: 'warn',
      },
      ...exportAction,
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ];
  }, [
    t,
    openCreateUserModal,
    openIssueInvoiceModal,
    isExportEnabled,
    isManagement,
    selectedTabKey,
    appliedFilters,
    openExportModal,
    setFiltersOpened,
  ]);

  const renderTabContent = useCallback((): Nullable<ReactNode> => {
    switch (selectedTabKey) {
      case 'customer':
        return (
          <>
            <ServerTable
              columns={customerColumns}
              rowActions={rowActions}
              fetchData={useGetPaginatedUsersListingQuery}
              defaultFilters={{ ...transformUsersListFilters(appliedFilters), role: UserRoles.CUSTOMER }}
              rowKey="userId"
              search
              tableActions={tableActions}
              tableActionsPosition="prev"
              rowActionsLimit={3}
              defaultSearch={externalFilters.search?.value}
              tags={tags}
              onAllFiltersClear={clearAllFilters}
            />
            <UsersListFilter />
          </>
        );
      case 'debtor':
        return (
          <>
            <ServerTable
              columns={customerColumns}
              rowActions={rowActions}
              fetchData={useGetPaginatedUsersListingQuery}
              defaultFilters={{ ...transformUsersListFilters(appliedFilters), role: UserRoles.CUSTOMER, debt: true }}
              rowKey="userId"
              search
              tableActions={tableActions}
              tableActionsPosition="prev"
              rowActionsLimit={3}
              defaultSearch={externalFilters.search?.value}
              tags={tags}
              onAllFiltersClear={clearAllFilters}
            />
            <UsersListFilter />
          </>
        );
      case 'manager':
        return (
          <>
            <ServerTable
              columns={columns}
              rowActions={rowActions}
              fetchData={useGetPaginatedUsersListingQuery}
              defaultFilters={{ ...transformUsersListFilters(appliedFilters), role: UserRoles.MANAGER }}
              tableActions={tableActions}
              tableActionsPosition="prev"
              rowKey="userId"
              search
              rowActionsLimit={3}
              defaultSearch={externalFilters.search?.value}
              tags={tags}
              onAllFiltersClear={clearAllFilters}
            />
            <UsersListFilter />
          </>
        );
      case 'superuser':
        return (
          <>
            <ServerTable
              columns={columns}
              rowActions={rowActions}
              fetchData={useGetPaginatedUsersListingQuery}
              defaultFilters={{ ...transformUsersListFilters(appliedFilters), role: UserRoles.SUPERUSER }}
              tableActions={tableActions}
              tableActionsPosition="prev"
              rowKey="userId"
              search
              rowActionsLimit={3}
              defaultSearch={externalFilters.search?.value}
              tags={tags}
              onAllFiltersClear={clearAllFilters}
            />
            <UsersListFilter />
          </>
        );
      case 'partner':
        return (
          <>
            <ServerTable
              columns={partnerColumns}
              rowActions={partnerRowActions}
              fetchData={useGetPaginatedUsersListingQuery}
              defaultFilters={{ ...transformUsersListFilters(appliedFilters), role: UserRoles.PARTNER }}
              rowKey="userId"
              search
              rowActionsLimit={2}
              tableActionsPosition="prev"
              tableActions={tableActions}
              defaultSearch={externalFilters.search?.value}
              tags={tags}
              onAllFiltersClear={clearAllFilters}
            />
            <UsersListFilter />
          </>
        );
      case 'staff':
        return (
          <>
            <ServerTable
              columns={columns}
              rowActions={rowActions}
              fetchData={useGetPaginatedUsersListingQuery}
              defaultFilters={{ ...transformUsersListFilters(appliedFilters), role: UserRoles.STAFF }}
              rowKey="userId"
              search
              rowActionsLimit={3}
              tableActionsPosition="prev"
              tableActions={tableActions}
              defaultSearch={externalFilters.search?.value}
              tags={tags}
              onAllFiltersClear={clearAllFilters}
            />
            <UsersListFilter />
          </>
        );
      case 'lead':
        return <LeadsTable externalFilters={externalFilters} />;
      default:
        return null;
    }
  }, [
    selectedTabKey,
    customerColumns,
    rowActions,
    appliedFilters,
    tableActions,
    externalFilters,
    tags,
    clearAllFilters,
    columns,
    partnerColumns,
    partnerRowActions,
  ]);

  const switchTab = useCallback(
    (tabKey: string): void => {
      setSelectedTabKey(tabKey as TabKey);
      clearAllFilters();
    },
    [clearAllFilters],
  );

  return (
    <>
      <TabSwitcher tabs={tabs} selectedTabKey={selectedTabKey} onChangeTab={switchTab}>
        {renderTabContent()}
      </TabSwitcher>
      <ExportModal />
      <SendOfferModal />
      <CreateUserModal />
      <CompleteRegistrationModal />
      <NoteDrawer />
      <EmailHistoryModal />
      <SendFranchiseSectionModal />
      <ChooseUserModal />
      <UserNoteDrawer />
      <IssueInvoiceModal />
    </>
  );
});
